import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { ApiBaseUrl } from "../utils/Constants";

const authHeaders = () => ({ Authorization: localStorage.getItem("token") });

// Save a new session
export const saveAssessmentSession = createAsyncThunk(
  "assessmentSessions/save",
  async (payload) => {
    const response = await axios.post(
      ApiBaseUrl + "/v1/user/assessment-sessions",
      payload,
      { headers: authHeaders() }
    );
    return response.data;
  }
);

// Get list of sessions for a project
export const getAssessmentSessions = createAsyncThunk(
  "assessmentSessions/list",
  async ({ projectId }) => {
    const response = await axios.get(
      ApiBaseUrl + `/v1/user/assessment-sessions?projectId=${projectId}`,
      { headers: authHeaders() }
    );
    return response.data;
  }
);

// Load a single session with chat history
export const loadAssessmentSession = createAsyncThunk(
  "assessmentSessions/load",
  async ({ sessionId }) => {
    const response = await axios.get(
      ApiBaseUrl + `/v1/user/assessment-sessions/${sessionId}`,
      { headers: authHeaders() }
    );
    return response.data;
  }
);

// Send a message in a session
export const assessmentSessionChat = createAsyncThunk(
  "assessmentSessions/chat",
  async (payload) => {
    const response = await axios.post(
      ApiBaseUrl + "/v1/user/assessment-sessions/chat",
      payload,
      { headers: authHeaders() }
    );
    return response.data;
  }
);

const assessmentSessionSlice = createSlice({
  name: "assessmentSessions",
  initialState: {
    sessions: [],
    currentSession: null,
    isSaving: false,
    isLoading: false,
    isChatLoading: false,
    error: null,
  },
  reducers: {
    clearCurrentSession: (state) => {
      state.currentSession = null;
    },
    appendChatMessage: (state, action) => {
      if (state.currentSession) {
        state.currentSession.chatHistory = [
          ...(state.currentSession.chatHistory || []),
          action.payload,
        ];
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Save
      .addCase(saveAssessmentSession.pending, (state) => { state.isSaving = true; state.error = null; })
      .addCase(saveAssessmentSession.fulfilled, (state, action) => {
        state.isSaving = false;
        if (action.payload?.status === 1) {
          state.sessions = [action.payload.session, ...state.sessions];
        }
      })
      .addCase(saveAssessmentSession.rejected, (state) => { state.isSaving = false; })
      // List
      .addCase(getAssessmentSessions.pending, (state) => { state.isLoading = true; })
      .addCase(getAssessmentSessions.fulfilled, (state, action) => {
        state.isLoading = false;
        if (action.payload?.status === 1) state.sessions = action.payload.sessions;
      })
      .addCase(getAssessmentSessions.rejected, (state) => { state.isLoading = false; })
      // Load single
      .addCase(loadAssessmentSession.pending, (state) => { state.isLoading = true; })
      .addCase(loadAssessmentSession.fulfilled, (state, action) => {
        state.isLoading = false;
        if (action.payload?.status === 1) state.currentSession = action.payload.session;
      })
      .addCase(loadAssessmentSession.rejected, (state) => { state.isLoading = false; })
      // Chat
      .addCase(assessmentSessionChat.pending, (state) => { state.isChatLoading = true; })
      .addCase(assessmentSessionChat.fulfilled, (state, action) => {
        state.isChatLoading = false;
        if (action.payload?.status === 1 && state.currentSession) {
          state.currentSession.chatHistory = [
            ...(state.currentSession.chatHistory || []),
            {
              role: "assistant",
              content: action.payload.reply,
              sources: action.payload.sources || [],
              createdAt: new Date().toISOString(),
            },
          ];
        }
      })
      .addCase(assessmentSessionChat.rejected, (state) => { state.isChatLoading = false; })
  },
});

export const { clearCurrentSession, appendChatMessage } = assessmentSessionSlice.actions;
export default assessmentSessionSlice.reducer;